/**
 * Hook useCalendarPermissions
 *
 * Lê e atualiza a matriz de permissões [perfil x permissão] de um calendário
 * específico via RPCs `get_calendar_permissions` e `update_calendar_permissions`.
 *
 * RELEASE:
 *   2026-06-25 v1.1.0 - Suporte a restrição por perfil de permissão
 *                        (profile_id/profile_name + payload profile_ids[]).
 *   2026-06-24 v1.0.0 - Versão inicial (Fase 5 Calendário Global)
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';

const logger = createLogger('useCalendarPermissions');

export type CalendarRole =
  | 'tenant_admin'
  | 'tenant_supervisor'
  | 'agent'
  | 'user';

export type CalendarPermission =
  | 'none'
  | 'free_busy'
  | 'view'
  | 'edit'
  | 'manage';

export interface CalendarPermissionRow {
  role: CalendarRole;
  permission: Exclude<CalendarPermission, 'none'>;
  profile_id: string | null;
  profile_name: string | null;
  updated_at: string;
}

/** Perfis renderizados na matriz (root_admin é excluído — acesso global). */
export const CALENDAR_MATRIX_ROLES: CalendarRole[] = [
  'tenant_admin',
  'tenant_supervisor',
  'agent',
  'user',
];

export const CALENDAR_PERMISSION_LABELS: Record<CalendarPermission, string> = {
  none: 'Nenhuma',
  free_busy: 'Apenas livre/ocupado',
  view: 'Visualizar',
  edit: 'Editar eventos',
  manage: 'Gerenciar',
};

export function useCalendarPermissions(calendarId: string | null | undefined) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  const tenantId = currentTenant?.id;

  const query = useQuery({
    queryKey: ['calendar-permissions', calendarId, tenantId],
    enabled: !!calendarId && !!tenantId,
    staleTime: 30_000,
    queryFn: async () => {
      logger.info('Carregando matriz de permissões', { calendarId });
      const { data, error } = await supabase.rpc('get_calendar_permissions', {
        p_calendar_id: calendarId!,
        p_tenant_hint: tenantId ?? null,
      });
      if (error) {
        logger.error('Erro ao carregar matriz de permissões', error);
        throw error;
      }
      return (data ?? []) as CalendarPermissionRow[];
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (
      matrix: Array<{
        role: CalendarRole;
        permission: CalendarPermission;
        profile_ids?: string[];
      }>,
    ) => {
      if (!calendarId) throw new Error('calendarId obrigatório');
      const { error } = await supabase.rpc('update_calendar_permissions', {
        p_calendar_id: calendarId,
        p_permissions: matrix as any,
        p_tenant_hint: tenantId ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-permissions', calendarId] });
      queryClient.invalidateQueries({ queryKey: ['user-calendars'] });
      toast.success('Permissões atualizadas');
    },
    onError: (e: any) => {
      logger.error('Erro ao salvar permissões', e);
      toast.error(e?.message ?? 'Erro ao salvar permissões');
    },
  });

  return {
    permissions: query.data ?? [],
    isLoading: query.isLoading,
    save: updateMutation.mutateAsync,
    isSaving: updateMutation.isPending,
  };
}
